import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ChatLauncher from './components/ChatLauncher';
import Home from './pages/Home';
import Palvelut from './pages/Palvelut';
import Meista from './pages/Meista';
import Yhteys from './pages/Yhteys';
import Ukk from './pages/Ukk';
import Tietosuoja from './pages/Tietosuoja';
import Kayttoehdot from './pages/Kayttoehdot';
import NotFound from './pages/NotFound';
import { initConsent } from './lib/consent';
import './App.css';

// Tracks Meta Pixel PageView + scroll to top/anchor on SPA route changes.
const RouteEffects = () => {
  const location = useLocation();

  // Pixel PageView — only fires if fbq has been loaded (post-consent)
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);

  // Scroll handling: top of page, or smooth to hash anchor
  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 8) {
          setTimeout(() => tryScroll(attempts + 1), 80);
        }
      };
      tryScroll();
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [location.pathname, location.hash]);

  return null;
};

function App() {
  useEffect(() => {
    // Initialise consent-aware analytics + noindex on preview hosts.
    // Voiceflow is NOT auto-loaded here — it loads only when the user
    // clicks the ChatLauncher (see lib/voiceflow.js).
    initConsent();

    // Hide the (Voiceflow) launcher whenever the user is on the contact form
    // OR the cookie banner is open. Sets body[data-vf-hidden] which the
    // Voiceflow stylesheet honours.
    const setVfHidden = (hidden) => {
      document.body.dataset.vfHidden = hidden ? 'true' : 'false';
    };

    const isFormFocused = () => {
      const active = document.activeElement;
      if (!active) return false;
      const form = active.closest && active.closest('[data-contact-form]');
      return !!form;
    };

    const isFormNearViewport = () => {
      const anchor = document.getElementById('contact-form');
      if (!anchor) return false;
      const rect = anchor.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < vh * 0.9 && rect.bottom > vh * 0.1;
    };

    const updateVfVisibility = () => {
      setVfHidden(isFormFocused() || isFormNearViewport());
    };

    window.addEventListener('scroll', updateVfVisibility, { passive: true });
    window.addEventListener('resize', updateVfVisibility);
    document.addEventListener('focusin', updateVfVisibility);
    document.addEventListener('focusout', updateVfVisibility);
    const t0 = setTimeout(updateVfVisibility, 600);

    return () => {
      window.removeEventListener('scroll', updateVfVisibility);
      window.removeEventListener('resize', updateVfVisibility);
      document.removeEventListener('focusin', updateVfVisibility);
      document.removeEventListener('focusout', updateVfVisibility);
      clearTimeout(t0);
    };
  }, []);

  return (
    <LanguageProvider>
      <Router>
        <RouteEffects />
        <div className="App">
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/palvelut" element={<Palvelut />} />
            <Route path="/meista" element={<Meista />} />
            <Route path="/yhteys" element={<Yhteys />} />
            <Route path="/ukk" element={<Ukk />} />
            <Route path="/tietosuoja" element={<Tietosuoja />} />
            <Route path="/kayttoehdot" element={<Kayttoehdot />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <CookieConsent />
          <ChatLauncher />
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;
